import * as React from "react";
import { toast } from "@/components/ui/toaster";
import { berlinHourKey } from "./hourly-due-rooms";

/**
 * useHourlyMeasureNudge — B7, sanfter zeitbasierter Stunden-Nudge solange das
 * Dashboard offen ist UND ≥1 Kritisch-Raum diese Stunde noch nicht gemessen ist.
 *
 * Muster angelehnt an auto-toast-on-level-change.ts (localStorage-throttle,
 * fail-open). Unterschiede, weil der Nudge ZEITbasiert statt event-basiert ist:
 *   - Feuert AUCH beim Initial-Mount (anders als level-rise), aber dank Throttle
 *     max. 1× pro (orgId, Berlin-Stunde) — kein Spam bei reload/route-rückkehr.
 *   - `setInterval(60s)` re-checkt, damit ein Stunden-Rollover bei dauerhaft
 *     offenem Dashboard einen frischen Nudge auslöst.
 *
 * Throttle-Key `hp.hourly-nudge.<orgId>` speichert den zuletzt genudgten
 * Berlin-Stunden-Bucket. Zusätzlich hält ein in-memory-ref den letzten Bucket
 * als Fallback: ohne localStorage (private-mode/quota) würde sonst JEDER 60s-
 * tick feuern — der ref drosselt dann wenigstens innerhalb der Session.
 *
 * Wortlaut bewusst Empfehlung, kein Score: "Stufe Kritisch hält an – stündliche
 * Messung empfohlen (ASR A3.5)".
 */
const STORAGE_KEY_PREFIX = "hp.hourly-nudge.";
const NUDGE_MESSAGE =
  "Stufe Kritisch hält an – stündliche Messung empfohlen (ASR A3.5)";
const CHECK_INTERVAL_MS = 60_000;

function storageKey(orgId: string): string {
  return `${STORAGE_KEY_PREFIX}${orgId}`;
}

function readLastBucket(orgId: string): string | null {
  try {
    return window.localStorage.getItem(storageKey(orgId));
  } catch {
    return null;
  }
}

function writeLastBucket(orgId: string, bucket: string): void {
  try {
    window.localStorage.setItem(storageKey(orgId), bucket);
  } catch {
    // fail-silent — der in-memory-ref übernimmt das throttling diese session
  }
}

export function useHourlyMeasureNudge(
  openCount: number,
  orgId: string | undefined,
): void {
  // Letzter genudgter Bucket in-memory — überlebt re-renders/effect-reruns
  // derselben component-instanz (nicht aber unmount), als localStorage-Fallback.
  const lastFiredBucketRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!orgId) return;
    const activeOrgId = orgId;

    function check(): void {
      if (openCount <= 0) return;
      const bucket = berlinHourKey(new Date());
      if (bucket === null) return; // Bucket nicht berechenbar → nicht feuern (kein Spam)
      const effectiveLast = readLastBucket(activeOrgId) ?? lastFiredBucketRef.current;
      if (bucket === effectiveLast) return; // diese Stunde schon genudgt
      toast(NUDGE_MESSAGE, "info");
      lastFiredBucketRef.current = bucket;
      writeLastBucket(activeOrgId, bucket);
    }

    check(); // initial-mount + bei openCount/orgId-wechsel
    const id = window.setInterval(check, CHECK_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [openCount, orgId]);
}
